import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Music, Music2, Volume2, VolumeX } from "lucide-react";
import { useSettings } from "@/lib/settings";

const THEMES = [
  {
    id: "calm",
    label: "Calm / Classical",
    desc: "Soft chimes, sine harps, gentle arpeggios",
    Icon: Music2,
  },
  {
    id: "house",
    label: "Strong Beat / House",
    desc: "Punchy 808, snappy hi-hats, synth stabs",
    Icon: Music,
  },
];

export default function SettingsDialog({ open, onOpenChange }) {
  const { settings, update, play } = useSettings();

  const pick = (id) => {
    update({ theme: id });
    setTimeout(() => play("start"), 20);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--surface))] border-white/10 text-white max-w-lg rounded-3xl p-0 overflow-hidden"
        data-testid="settings-dialog"
      >
        <div className="h-1 bg-[hsl(var(--green))]" />
        <div className="p-7">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tight">
              Sound & Effects
            </DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              Dopamine, tuned to your taste.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-6">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/40 mb-3">
              Sound theme
            </div>
            <div className="grid grid-cols-1 gap-2">
              {THEMES.map((t) => {
                const on = settings.theme === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => pick(t.id)}
                    className={`text-left p-4 rounded-2xl border transition flex items-center gap-4 ${
                      on
                        ? "border-[hsl(var(--green))]/60 bg-[hsl(var(--green))]/10"
                        : "border-white/10 bg-black/20 hover:border-white/30"
                    }`}
                    data-testid={`theme-option-${t.id}`}
                  >
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        on
                          ? "bg-[hsl(var(--green))]/20 text-[hsl(var(--green))]"
                          : "bg-white/5 text-white/60"
                      }`}
                    >
                      <t.Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-base">{t.label}</div>
                      <div className="text-xs text-white/50">{t.desc}</div>
                    </div>
                    <div
                      className={`w-3 h-3 rounded-full ${
                        on ? "bg-[hsl(var(--green))]" : "bg-white/15"
                      }`}
                    />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between p-4 rounded-2xl bg-black/20 border border-white/10">
            <div className="flex items-center gap-3">
              {settings.muted ? (
                <VolumeX className="w-5 h-5 text-white/50" />
              ) : (
                <Volume2 className="w-5 h-5 text-[hsl(var(--green))]" />
              )}
              <div>
                <div className="font-display font-semibold">Sound</div>
                <div className="text-xs text-white/50">
                  {settings.muted ? "All effects muted" : "Effects on"}
                </div>
              </div>
            </div>
            <Switch
              checked={!settings.muted}
              onCheckedChange={(v) => update({ muted: !v })}
              data-testid="settings-mute-toggle"
            />
          </div>

          <div className="mt-4 p-4 rounded-2xl bg-black/20 border border-white/10">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/50">
                Volume
              </div>
              <div className="font-mono text-xs text-white/50">
                {Math.round(settings.volume * 100)}%
              </div>
            </div>
            <Slider
              value={[settings.volume * 100]}
              onValueChange={(v) => update({ volume: (v?.[0] ?? 0) / 100 })}
              max={100}
              step={1}
              data-testid="settings-volume-slider"
            />
          </div>

          <div className="mt-6 flex gap-2">
            <Button
              onClick={() => play("complete")}
              className="flex-1 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl h-10"
              data-testid="settings-preview-button"
            >
              Preview
            </Button>
            <Button
              onClick={() => onOpenChange(false)}
              className="flex-1 bg-[hsl(var(--green))]/15 hover:bg-[hsl(var(--green))]/25 border border-[hsl(var(--green))]/40 text-[hsl(var(--green))] rounded-xl h-10"
              data-testid="settings-close-button"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
